// Lo que se dibuja ENCIMA de una celda mientras hay una mezcla armándose (spec §3.5,
// ADR-2026-09-17-mezclar-es-un-modo-del-lienzo, ADR-2026-09-17-la-mezcla-no-se-guarda):
// el anillo y el rótulo sobre la prenda con su botón de Generar, el número sobre cada
// parte, y A LA DERECHA de cada parte la cajita "¿qué tomar de esta imagen?". La cajita
// va por `NodeToolbar` (un portal que React Flow NO escala con el zoom y que no queda
// recortado por el `overflow-hidden` de la foto), como la barra de acciones. Se monta
// por la ranura `Adorno` del lienzo. También lo pintado con el MARCADOR
// (ADR-2026-09-21-el-marcador-de-la-mezcla): con el Marcador de la barra prendido,
// arrastrar sobre una imagen de la mezcla la pinta con un pincel rojo semitransparente;
// lo pintado queda encima (un canvas a escala de la foto). En la base dice dónde va el
// cambio, en una parte qué tomar de esa imagen.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NodeToolbar, Position, useViewport } from '@xyflow/react';
import { Blend, Eraser } from 'lucide-react';
import { CreditBadge } from '../../../../components/CreditBadge';
import { useExitTransition } from '../../../../hooks/useExitTransition';
import { anchoDeCelda } from '../../../../chat-atlas/lienzo/acomodo';
import { useDiseno } from './disenoContext';
import { TOPE_INSTRUCCION } from './promptsDeDiseno';
import { conPunto, conTrazo, marcaVacia, type Marca, type PuntoDeMarca } from './marcador';
import { COLOR_DE_MARCA, pintarMarca } from './imagenConMarca';

const detener = (e: React.SyntheticEvent) => e.stopPropagation();

/** Aire entre la foto y "Generar mezcla" (el mismo que el pie con el título, al que reemplaza), en unidades del lienzo. */
const SEPARACION_GENERAR = 12;
const MS_GENERAR = 200;

const BOTON_DE_MARCA =
  'nodrag nopan inline-flex items-center gap-1.5 rounded-full py-1 px-2.5 text-[12px] font-semibold transition-colors bg-white text-[#02062B] shadow hover:bg-[#F3F4F8]';

/** El punto del puntero en coordenadas relativas a la caja (0..1). */
const puntoRelativo = (e: React.PointerEvent<HTMLElement>): PuntoDeMarca => {
  const r = e.currentTarget.getBoundingClientRect();
  return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
};

/**
 * Lo pintado, encima de la foto: un canvas del tamaño de la celda (a 2× para que no se
 * vea pixelado con zoom) que se redibuja con la marca y con el trazo en curso.
 */
const Pintura: React.FC<{ marca: Marca | undefined; enCurso: PuntoDeMarca[] | null }> = ({ marca, enCurso }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [medida, setMedida] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setMedida({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || medida.w === 0 || medida.h === 0) return;
    const escala = 2;
    el.width = medida.w * escala;
    el.height = medida.h * escala;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, el.width, el.height);
    const conEnCurso: Marca = {
      ...(marca ?? marcaVacia()),
      trazos: [...(marca?.trazos ?? []), ...(enCurso && enCurso.length > 0 ? [enCurso] : [])],
    };
    if (conEnCurso.trazos.length > 0) pintarMarca(ctx, conEnCurso, el.width, el.height);
  }, [marca, enCurso, medida]);
  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />;
};

// La cajita arranca con 2 líneas y crece con lo escrito hasta 5; de ahí en más scrollea
// (pedido de Matías del 2026-09-21). Línea de 20px (`leading-5`) + `py-2` (8px arriba y abajo).
const ALTO_DE_LINEA = 20;
const LINEAS_MIN = 2;
const LINEAS_MAX = 5;
const altoDeLineas = (n: number) => n * ALTO_DE_LINEA + 16;
const ajustarAlto = (el: HTMLTextAreaElement) => {
  // `box-sizing: border-box`: el alto fijado incluye el borde, y `scrollHeight` no. Sin
  // sumarlo, la caja quedaba 2px más chica que su contenido y el scrollbar asomaba
  // desde la primera línea.
  const borde = el.offsetHeight - el.clientHeight;
  el.style.overflowY = 'hidden';
  el.style.height = `${altoDeLineas(LINEAS_MIN) + borde}px`;
  const contenido = el.scrollHeight;
  const tope = altoDeLineas(LINEAS_MAX);
  el.style.height = `${Math.min(Math.max(contenido, altoDeLineas(LINEAS_MIN)), tope) + borde}px`;
  // El scroll sólo a partir de la sexta línea.
  el.style.overflowY = contenido > tope ? 'auto' : 'hidden';
};

export const AdornoDeMezcla: React.FC<{ id: string; ratio: number }> = ({ id, ratio }) => {
  // El botón mide lo que mide la celda, que ya no es un ancho común
  // (ADR-2026-09-22-las-celdas-del-lienzo-ocupan-la-misma-superficie).
  const anchoCelda = anchoDeCelda(ratio);
  const d = useDiseno();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const mezcla = d.mezcla;
  const esBase = mezcla?.baseId === id;
  const indice = mezcla ? mezcla.partes.findIndex((p) => p.celdaId === id) : -1;
  const enEdicion = d.parteEnEdicion === id;

  useEffect(() => {
    if (enEdicion) inputRef.current?.select();
  }, [enEdicion]);
  const instruccion = indice >= 0 ? mezcla?.partes[indice]?.instruccion : undefined;
  useEffect(() => {
    if (inputRef.current) ajustarAlto(inputRef.current);
  }, [instruccion]);

  // El trazo en curso vive acá (no en la mezcla): un `setMezcla` por cada movimiento del
  // puntero re-renderizaría el lienzo entero; la mezcla recibe el trazo al soltar.
  const [enCurso, setEnCurso] = useState<PuntoDeMarca[] | null>(null);
  const enLaMezcla = !!mezcla && (esBase || indice >= 0);
  const pintando = enLaMezcla && d.marcador;
  // "Generar mezcla" debajo de la base, en el lugar del título, apenas hay una parte:
  // entra y sale con una transición corta.
  const conGenerar = !!mezcla && esBase && mezcla.partes.length > 0;
  const { mounted: generarMontado, animated: generarVisible } = useExitTransition(conGenerar, MS_GENERAR);
  // `NodeToolbar` no escala con el zoom: el botón se escala a mano para quedar SIEMPRE
  // del ancho de la foto y pegado a ella, como si fuera parte de la celda.
  const { zoom } = useViewport();
  useEffect(() => {
    if (!pintando) setEnCurso(null);
  }, [pintando]);

  if (!mezcla || (!esBase && indice < 0)) return null;
  const parte = indice >= 0 ? mezcla.partes[indice] : null;
  const marca = esBase ? mezcla.marcaBase : parte?.marca;
  const fijarMarca = (m: Marca | undefined) =>
    d.setMezcla((mz) => {
      if (!mz) return mz;
      if (esBase) return { ...mz, marcaBase: m };
      return { ...mz, partes: mz.partes.map((p) => (p.celdaId === id ? { ...p, marca: m } : p)) };
    });
  const botonBorrar = marca ? (
    <button
      type="button"
      onClick={(e) => {
        detener(e);
        fijarMarca(undefined);
      }}
      onPointerDown={detener}
      className={BOTON_DE_MARCA}
      title="Borrar lo pintado"
    >
      <Eraser className="h-3.5 w-3.5" style={{ color: COLOR_DE_MARCA }} aria-hidden="true" />
      Borrar marca
    </button>
  ) : null;
  return (
    <>
      <span
        className={`pointer-events-none absolute inset-0 rounded-2xl ring-inset ${esBase ? 'ring-4 ring-brand-primary' : 'ring-4 ring-[#1644EB]/[0.45]'}`}
        aria-hidden="true"
      />
      {(marca || enCurso) && <Pintura marca={marca} enCurso={enCurso} />}
      {pintando && (
        <div
          className="nodrag nopan absolute inset-0 z-10"
          style={{ cursor: 'crosshair', touchAction: 'none' }}
          onClick={detener}
          onDoubleClick={detener}
          onPointerDown={(e) => {
            detener(e);
            e.currentTarget.setPointerCapture(e.pointerId);
            setEnCurso(conPunto([], puntoRelativo(e)));
          }}
          onPointerMove={(e) => {
            if (!enCurso) return;
            const p = puntoRelativo(e);
            setEnCurso((t) => (t ? conPunto(t, p) : t));
          }}
          onPointerUp={(e) => {
            detener(e);
            if (!enCurso) return;
            const trazo = conPunto(enCurso, puntoRelativo(e));
            setEnCurso(null);
            // Un click sin arrastre no pinta nada (`conTrazo` lo descarta).
            fijarMarca(conTrazo(marca, trazo));
          }}
          onPointerCancel={() => setEnCurso(null)}
        />
      )}
      <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-brand-primary px-2.5 py-1 text-[12px] font-semibold text-white shadow">
        {esBase ? `Mezclando · ${mezcla.partes.length}` : `${indice + 2}`}
      </span>
      {esBase && botonBorrar && <span className="absolute right-2 top-2 z-20">{botonBorrar}</span>}
      {esBase && generarMontado && (
        <NodeToolbar isVisible position={Position.Bottom} align="start" offset={SEPARACION_GENERAR * zoom}>
          {/* El envoltorio lleva la escala del zoom SIN transición (si no, el botón iría a
              la zaga del zoom); el fundido de entrada y salida es del botón. */}
          <div style={{ width: anchoCelda * zoom, height: 36 * zoom }}>
            <div style={{ width: anchoCelda, transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
              <button
                type="button"
                onClick={(e) => {
                  detener(e);
                  d.generarMezcla();
                }}
                onPointerDown={detener}
                className={`nodrag nopan inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-2 px-4 text-[13px] font-semibold text-white shadow-lg transition-[opacity,transform,background-color] duration-200 ease-out hover:bg-brand-primary-hover ${
                  generarVisible ? 'translate-y-0 opacity-100' : '-translate-y-1.5 opacity-0'
                }`}
              >
                <Blend className="h-4 w-4" aria-hidden="true" />
                Generar mezcla
                <CreditBadge credits={mezcla.cantidad} tone="on-primary" size="sm" />
              </button>
            </div>
          </div>
        </NodeToolbar>
      )}
      {parte && (
        <NodeToolbar isVisible position={Position.Right} align="start" offset={12}>
          <div
            className="nodrag nopan w-[320px] rounded-2xl border border-black/[0.06] bg-white p-3 shadow-[0_6px_20px_rgba(2,6,43,0.12)]"
            onClick={detener}
            onPointerDown={detener}
          >
            <p className="mb-2 text-[14px] font-bold text-brand-black">¿Qué tomar de esta imagen?</p>
            <textarea
              ref={inputRef}
              rows={LINEAS_MIN}
              autoFocus={enEdicion}
              // Tal cual se escribe, espacios incluidos: guardarla recortada (`trim`) desde
              // un input controlado devolvía el valor sin el espacio final y no se podía
              // escribir una segunda palabra (ADR-2026-09-17-la-mezcla-no-se-guarda).
              value={parte.instruccion}
              onChange={(e) => {
                const texto = e.target.value.slice(0, TOPE_INSTRUCCION);
                d.setMezcla((m) => (m ? { ...m, partes: m.partes.map((p) => (p.celdaId === id ? { ...p, instruccion: texto } : p)) } : m));
              }}
              onKeyDown={(e) => {
                // Enter confirma; Shift+Enter baja de línea.
                if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
                  e.preventDefault();
                  // La hoja escucha Escape en `document`: si no cortamos la propagación acá,
                  // el mismo Escape que suelta la cajita también cierra la hoja de Mezclar.
                  if (e.key === 'Escape') e.stopPropagation();
                  d.setParteEnEdicion(null);
                  inputRef.current?.blur();
                }
              }}
              placeholder="la textura, el color, la persona que la usa…"
              className="block w-full resize-none overflow-y-hidden rounded-xl border border-black/[0.08] px-3 py-2 text-[14px] leading-5 text-brand-black outline-none focus:border-brand-primary"
            />
            {botonBorrar && <div className="mt-2 flex justify-end [&>button]:border [&>button]:border-black/[0.08] [&>button]:shadow-none">{botonBorrar}</div>}
          </div>
        </NodeToolbar>
      )}
    </>
  );
};
